<script setup>
import appAxios from '@/libs/app-axios'
import { useStore } from '@/store'
import { useRouter } from 'vue-router/composables'
import { computed, defineExpose, onBeforeMount, reactive, ref, watchEffect } from 'vue'

const store = useStore()
const router = useRouter()
const authAccessLevel = computed(() => store.state.auth.accessLevel)

/** @type {TableState} */
const initialTableState = {
  items: [],
  isLoading: false,
  meta: null,
}

const tableState = reactive({ ...initialTableState, items: [], meta: null })

/** @type {FilterState} */
const filters = reactive({
  customerId: null,
  year: null,
})

/** @type {CustomerDropdown} */
const customerState = reactive({
  items: [],
  isLoading: false,
})

/** @type {import('vue').Ref<Set<number>>} */
const printingSet = ref(new Set())
/** @type {import('vue').Ref<Set<number>>} */
const deletingSet = ref(new Set())

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i)

const getCustomers = async () => {
  try {
    customerState.isLoading = true
    const { data } = await appAxios().get('/v3/customers/dropdown-options')
    customerState.items = data.data || []
  } catch (e) {
    setTimeout(() => getCustomers(), 3000)
  } finally {
    customerState.isLoading = false
  }
}

/**
 * @param {AbortController} [controller]
 */
const getList = async controller => {
  try {
    tableState.isLoading = true
    const { data } = await appAxios().get('/v3/pm-summaries', {
      signal: controller?.signal,
      params: {
        customer_id: filters.customerId || undefined,
        year: filters.year || undefined,
        per_page: 50,
      },
    })
    tableState.items = data.data
    tableState.meta = data.meta
  } catch (e) {
    if (e?.name === 'CanceledError' || e?.name === 'AbortError') return
    setTimeout(() => getList(), 3000)
  } finally {
    tableState.isLoading = false
  }
}

const onPdfHandler = async id => {
  if (printingSet.value.has(id)) return
  const updated = new Set(printingSet.value)
  updated.add(id)
  printingSet.value = updated
  try {
    const { data } = await appAxios().get(`/v3/pm-summaries/${id}/pdf`, { responseType: 'blob' })
    const blob = new Blob([data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (e) {
    alert(e?.message || 'Failed to generate PDF')
  } finally {
    const next = new Set(printingSet.value)
    next.delete(id)
    printingSet.value = next
  }
}

/**
 * @param {PmSummaryRow} item
 */
const onDeleteHandler = async item => {
  const confirmText = `DELETE-${item.id}`
  const input = prompt(`This action is irreversible.\n\nType "${confirmText}" to confirm deletion.`)
  if (input !== confirmText) {
    alert('Deletion cancelled. Confirmation text did not match.')
    return
  }
  const updated = new Set(deletingSet.value)
  updated.add(item.id)
  deletingSet.value = updated
  try {
    await appAxios().delete(`/v3/pm-summaries/${item.id}`)
    tableState.items = tableState.items.filter(i => i.id !== item.id)
  } catch (e) {
    alert(e?.message || 'Failed to delete')
  } finally {
    const next = new Set(deletingSet.value)
    next.delete(item.id)
    deletingSet.value = next
  }
}

// pm_month arrives as a plain YYYY-MM-DD date. Build the Date from its parts so
// it is never reinterpreted against the browser timezone.
const formatPmMonth = value => {
  if (!value) return ''
  const [year, month] = value.split('-')
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  })
}

/** @type {Array<import('vuetify').DataTableHeader>} */
const headers = [
  { text: 'Customer', value: 'customer' },
  { text: 'PM Month', value: 'pm_month' },
  { text: 'Subject', value: 'subject' },
  { text: 'Prepared By', value: 'prepared_by_name' },
  { text: 'Actions', value: 'action', sortable: false },
]

watchEffect(onCleanup => {
  filters.customerId
  filters.year
  const controller = new AbortController()
  onCleanup(() => controller.abort())
  getList(controller)
})

onBeforeMount(() => {
  getCustomers()
})

defineExpose({ getList })
</script>

<template>
  <div class="pm-summary-index">
    <div class="filters">
      <v-autocomplete
        outlined
        dense
        clearable
        label="Filter Customer"
        v-model="filters.customerId"
        :items="customerState.items"
        item-value="id"
        item-text="company"
        :loading="customerState.isLoading"
        prepend-icon="mdi-magnify"
        hide-details
      />
      <v-select
        outlined
        dense
        clearable
        label="Filter Year"
        v-model="filters.year"
        :items="yearOptions"
        hide-details
      />
    </div>

    <v-data-table
      :loading="tableState.isLoading"
      :items="tableState.items"
      :headers="headers"
      :sort-by="['pm_month']"
      :sort-desc="[true]"
    >
      <template #item.customer="{ item }">
        {{ item.customer ? item.customer.name : item.customer_id }}
      </template>
      <template #item.pm_month="{ item }">
        {{ formatPmMonth(item.pm_month) }}
      </template>
      <template #item.action="{ item }">
        <div style="display: flex; gap: 5px;">
          <v-btn icon outlined color="red" :loading="printingSet.has(item.id)" @click="onPdfHandler(item.id)">
            <v-icon>mdi-file-pdf-box</v-icon>
          </v-btn>
          <v-btn icon outlined color="warning" @click="router.push({ name: 'preventive-maintenance.summary.edit', params: { id: item.id } })">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="authAccessLevel >= 7"
            icon
            outlined
            color="red"
            :loading="deletingSet.has(item.id)"
            @click="onDeleteHandler(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<style lang="sass" scoped>
.pm-summary-index
  display: flex
  flex-direction: column
  gap: 10px
  .filters
    display: flex
    gap: 10px
    flex-wrap: wrap
</style>

<script>
export default {
  name: 'PmSummaryIndex',
}
/**
 * @typedef {Object} PmSummaryRow
 * @property {number} id
 * @property {string} customer_id
 * @property {{ id: string, name: string, address: string }|null} customer
 * @property {string} pm_month
 * @property {string|null} subject
 * @property {string|null} attention_name
 * @property {string|null} prepared_by_name
 * @property {string|null} prepared_by_title
 * @property {string|null} approved_by_name
 * @property {string|null} approved_by_title
 * @property {string} created_at
 * @property {string} updated_at
 *
 * @typedef {{ items: PmSummaryRow[], isLoading: boolean, meta: object|null }} TableState
 * @typedef {{ items: {id: string, company: string}[], isLoading: boolean }} CustomerDropdown
 * @typedef {{ customerId: string|null, year: number|null }} FilterState
 */
</script>
